import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl } from "utils/apiUrl";
import ClientSuggestionsList from "components/client/ClientSuggestionsList";
import { clientSafeMessage } from "utils/clientSafeMessage";
import { readClientSession } from "utils/clientSession";

const TELEGRAM_SUPPORT_URL =
  (process.env.REACT_APP_TELEGRAM_SUPPORT_URL || "").trim() ||
  "https://t.me/AlphaSniperAdmin";

const labelClass =
  "block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400";
const inputClass =
  "mt-1.5 w-full rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-navy-800 outline-none transition placeholder:text-gray-400 focus:border-brand-400 focus:ring-2 focus:ring-brand-400/25 dark:border-white/15 dark:bg-navy-900 dark:text-white dark:placeholder:text-gray-500";

function formatDisplayName(session) {
  const first = String(session?.firstName || "").trim();
  const last = String(session?.lastName || "").trim();
  if (first && last) return `${first} ${last}`;
  return first || last || "";
}

function SupportSubnav({ active }) {
  const linkClass = (key) =>
    `rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
      active === key
        ? "bg-brand-500 text-white dark:bg-brand-400"
        : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10"
    }`;

  return (
    <nav className="flex flex-wrap gap-2" aria-label="Support sections">
      <Link to="/client/support" className={linkClass("help")}>
        Help & tickets
      </Link>
      <Link to="/client/support/suggestions" className={linkClass("suggestions")}>
        Suggestions
      </Link>
    </nav>
  );
}

export default function ClientSuggestions() {
  const session = readClientSession();
  const clientId =
    session?.clientId != null ? String(session.clientId) : "";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [title, setTitle] = useState("");
  const [idea, setIdea] = useState("");
  const [status, setStatus] = useState("idle");
  const [feedback, setFeedback] = useState(null);
  const [listRefresh, setListRefresh] = useState(0);

  useEffect(() => {
    const display = formatDisplayName(session);
    if (display) setName(display);
    if (session?.email) setEmail(String(session.email).trim());
  }, [session]);

  async function submitSuggestion(e) {
    e.preventDefault();
    setStatus("sending");
    setFeedback(null);
    try {
      const res = await fetch(apiUrl("/api/client/suggestions"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          title: title.trim(),
          message: idea.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        setFeedback({
          type: "error",
          text: clientSafeMessage(
            data.message,
            "Could not save your suggestion. Please try again.",
          ),
        });
        setStatus("idle");
        return;
      }
      setFeedback({ type: "success", text: data.message });
      setTitle("");
      setIdea("");
      setListRefresh((k) => k + 1);
      setStatus("idle");
    } catch {
      setFeedback({
        type: "error",
        text: "Network error. Check your connection and try again.",
      });
      setStatus("idle");
    }
  }

  return (
    <div className="space-y-6">
      <div className="rounded-[20px] border border-brand-200 bg-gradient-to-br from-brand-50 via-white to-white p-6 shadow-sm dark:border-brand-500/25 dark:from-brand-500/10 dark:via-navy-800 dark:to-navy-800">
        <SupportSubnav active="suggestions" />
        <p className="mt-4 text-lg font-bold leading-snug text-navy-800 dark:text-white sm:text-xl">
          To succeed together, we must listen to each other.
        </p>
        <p className="mt-2 max-w-2xl text-sm text-gray-600 dark:text-gray-400">
          Your ideas help us improve AlphaSniper for every trader. Share features
          you would like, things we could do better, or anything that would make
          your experience stronger. We read every suggestion.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_minmax(240px,280px)]">
        <section className="rounded-[20px] border border-gray-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-navy-800">
          <h3 className="text-lg font-bold text-navy-700 dark:text-white">
            Send a suggestion
          </h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Be as specific as you like. No passwords or account secrets.
          </p>

          {feedback ? (
            <div
              className={`mt-4 rounded-xl border px-4 py-3 text-sm font-semibold ${
                feedback.type === "success"
                  ? "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-950/40 dark:text-emerald-200"
                  : "border-red-200 bg-red-50 text-red-800 dark:border-red-500/30 dark:bg-red-950/40 dark:text-red-200"
              }`}
              role="status"
            >
              {feedback.text}
            </div>
          ) : null}

          <form className="mt-6 space-y-4" onSubmit={submitSuggestion} noValidate>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className={labelClass} htmlFor="suggestion-name">
                  Your name
                </label>
                <input
                  id="suggestion-name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  minLength={2}
                  maxLength={200}
                  value={name}
                  onChange={(ev) => setName(ev.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass} htmlFor="suggestion-email">
                  E-mail
                </label>
                <input
                  id="suggestion-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  className={inputClass}
                />
              </div>
            </div>

            <div>
              <label className={labelClass} htmlFor="suggestion-title">
                Short title (optional)
              </label>
              <input
                id="suggestion-title"
                name="title"
                type="text"
                maxLength={180}
                value={title}
                onChange={(ev) => setTitle(ev.target.value)}
                className={inputClass}
                placeholder="e.g. Dark mode for mobile, new broker, dashboard widget…"
              />
            </div>

            <div>
              <label className={labelClass} htmlFor="suggestion-idea">
                Your idea
              </label>
              <textarea
                id="suggestion-idea"
                name="idea"
                required
                minLength={20}
                maxLength={8000}
                rows={7}
                value={idea}
                onChange={(ev) => setIdea(ev.target.value)}
                className={`${inputClass} min-h-[160px] resize-y`}
                placeholder="Describe what you would like us to build or improve, and why it would help you."
              />
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-1">
              <button
                type="submit"
                disabled={status === "sending"}
                className="rounded-xl bg-brand-500 px-6 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-brand-400 dark:hover:bg-brand-300"
              >
                {status === "sending" ? "Sending…" : "Submit suggestion"}
              </button>
              <span className="text-xs text-gray-500 dark:text-gray-500">
                We may follow up by e-mail if we need clarification.
              </span>
            </div>
          </form>
        </section>

        <aside className="space-y-4">
          <div className="rounded-[20px] border border-gray-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-navy-800">
            <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Need help instead?
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              For bugs, billing, or account issues, use the support ticket form.
            </p>
            <Link
              to="/client/support"
              className="mt-4 inline-flex w-full items-center justify-center rounded-xl border-2 border-gray-200 bg-white px-4 py-2.5 text-sm font-bold text-navy-700 transition hover:border-brand-400 dark:border-white/15 dark:bg-navy-900 dark:text-white"
            >
              Open help & tickets
            </Link>
          </div>
          <div className="rounded-[20px] border border-gray-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-navy-800">
            <h3 className="text-sm font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Telegram
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Quick questions about your account or setup.
            </p>
            <a
              href={TELEGRAM_SUPPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex w-full items-center justify-center rounded-xl border-2 border-brand-500 bg-brand-500 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-brand-600 dark:border-brand-400 dark:bg-brand-400"
            >
              Message support
            </a>
          </div>
        </aside>
      </div>

      <section className="rounded-[20px] border border-gray-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-navy-800">
        <h3 className="text-lg font-bold text-navy-700 dark:text-white">
          All client suggestions
        </h3>
        <div className="mt-4">
          <ClientSuggestionsList
            refreshKey={listRefresh}
            showSubmitLink={false}
          />
        </div>
      </section>
    </div>
  );
}
